import React, { useEffect, useRef, useState } from 'react';
import { getSecondsUntilCutoff } from './expressDeliveryUtils';
import * as translator from '../../../../utils/translator';

export type CountdownTimerVariant = 'banner' | 'sameDayInline';

interface CountdownTimerProps {
  cutoffTime: string;
  onExpired: () => void;
  language: string;
  /** banner = legacy teal row above options; sameDayInline = orange line inside same-day option */
  variant?: CountdownTimerVariant;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  cutoffTime,
  onExpired,
  language,
  variant = 'banner',
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => {
    return getSecondsUntilCutoff(cutoffTime) ?? 0;
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cutoffMsRef = useRef<number>(Date.now() + (remainingSeconds * 1000));

  useEffect(() => {
    if (remainingSeconds <= 0) {
      onExpired();
      return;
    }

    cutoffMsRef.current = Date.now() + (remainingSeconds * 1000);

    intervalRef.current = setInterval(() => {
      const secondsLeft = Math.floor((cutoffMsRef.current - Date.now()) / 1000);
      if (secondsLeft <= 0) {
        setRemainingSeconds(0);
        if (intervalRef.current) clearInterval(intervalRef.current);
        onExpired();
      } else {
        setRemainingSeconds(secondsLeft);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (remainingSeconds <= 0) return null;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (variant === 'sameDayInline') {
    return (
      <div className="express-same-day-cutoff">
        <span className="express-same-day-cutoff-label">
          {translator.translate(language, "choose_treatment", "shipping_and_service_selection/same_day_cutoff_label")}
        </span>
        <span className="express-same-day-cutoff-time">{mm}:{ss}</span>
      </div>
    );
  }

  return (
    <div className="express-countdown">
      <span className="express-countdown-label">
        {translator.translate(language, "choose_treatment", "shipping_and_service_selection/countdown_label")}
      </span>
      <span className="express-countdown-time">{mm}:{ss}</span>
    </div>
  );
};

export default CountdownTimer;
